/**
 * Store de estado para fibras y cables
 * @module stores/useFiberStore
 */

import { create } from 'zustand'
import { Cable, TraceResult } from '@/types/fibers'
import { cableServiceApi } from '@/services/cableServiceApi'
import { traceServiceApi } from '@/services/traceServiceApi'

// ============================================================================
// TYPES
// ============================================================================

interface FiberStore {
  // Estado
  cables: Cable[]
  selectedCableId: string | null
  activeTrace: TraceResult | null
  loading: boolean
  traceLoading: boolean
  error: string | null

  // Acciones de cables
  init: () => Promise<void>
  loadCables: () => Promise<void>
  addCable: (cable: Cable) => Promise<Cable>
  updateCable: (cable: Cable) => Promise<void>
  deleteCable: (id: string) => Promise<void>
  selectCable: (id: string | null) => void
  setSelectedCable: (id: string | null) => void

  // Acciones de trazabilidad
  traceFiber: (fiberId: string) => Promise<void>
  traceFromClient: (clientId: string) => Promise<void>
  traceFromNode: (nodeId: string) => Promise<void>
  clearTrace: () => void
  clearError: () => void
}

// ============================================================================
// STORE
// ============================================================================

export const useFiberStore = create<FiberStore>((set, get) => ({
  // Estado inicial
  cables: [],
  selectedCableId: null,
  activeTrace: null,
  loading: false,
  traceLoading: false,
  error: null,

  // Acciones
  init: async () => {
    set({ loading: true, error: null })
    try {
      await get().loadCables()
      set({ loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error inicializando fibras',
        loading: false,
      })
    }
  },

  loadCables: async () => {
    set({ loading: true, error: null })
    try {
      const cables = await cableServiceApi.getAll()
      set({ cables, loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error cargando cables',
        loading: false,
      })
    }
  },

  addCable: async (cable: Cable): Promise<Cable> => {
    set({ loading: true, error: null })
    try {
      const createdCable = await cableServiceApi.create(cable)
      set((state) => ({
        cables: [...state.cables, createdCable],
        loading: false,
      }))
      return createdCable
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error agregando cable',
        loading: false,
      })
      throw error
    }
  },

  updateCable: async (cable: Cable) => {
    set({ loading: true, error: null })
    try {
      const updatedCable = await cableServiceApi.update(cable.id, cable)
      set((state) => ({
        cables: state.cables.map((c) => (c.id === cable.id ? updatedCable : c)),
        loading: false,
      }))
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error actualizando cable',
        loading: false,
      })
      throw error
    }
  },

  deleteCable: async (id: string) => {
    set({ loading: true, error: null })
    try {
      await cableServiceApi.delete(id)
      set((state) => ({
        cables: state.cables.filter((c) => c.id !== id),
        selectedCableId: state.selectedCableId === id ? null : state.selectedCableId,
        loading: false,
      }))
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error eliminando cable',
        loading: false,
      })
      throw error
    }
  },

  selectCable: (id: string | null) => {
    set({ selectedCableId: id })
  },

  setSelectedCable: (id: string | null) => {
    set({ selectedCableId: id })
  },

  // Trazabilidad usando API del servidor
  traceFiber: async (fiberId: string) => {
    set({ traceLoading: true, error: null })
    try {
      // Usar el fiberId como nodeId para iniciar la traza
      const trace = await traceServiceApi.traceFromNode(fiberId)
      set({ activeTrace: trace, traceLoading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error trazando fibra',
        activeTrace: null,
        traceLoading: false,
      })
    }
  },

  traceFromClient: async (clientId: string) => {
    set({ traceLoading: true, error: null })
    try {
      const trace = await traceServiceApi.traceFromClient(clientId)
      set({ activeTrace: trace, traceLoading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error trazando desde cliente',
        activeTrace: null,
        traceLoading: false,
      })
    }
  },

  traceFromNode: async (nodeId: string) => {
    set({ traceLoading: true, error: null })
    try {
      const trace = await traceServiceApi.traceFromNode(nodeId)
      set({ activeTrace: trace, traceLoading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error trazando desde nodo',
        activeTrace: null,
        traceLoading: false,
      })
    }
  },

  clearTrace: () => {
    set({ activeTrace: null })
  },

  clearError: () => {
    set({ error: null })
  },
}))
